import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Fab, Page } from 'framework7-react';

import Config from 'Config';
import { Plus } from 'Icons';
import { I18n } from 'Locales';
import { mockFunction } from 'Helpers';
import { ColumnView } from 'Containers';
import { NavigationService } from 'Services';
import { AppHeader, Banner, Button, Pagination, QuickFilters, Table } from 'Components';
import { ADMIN_ROLES, ALERT_TYPES, ALIGNMENT, FILTERS, PAGE_NAMES, TABLE_CONFIG, TEXT_ALIGN } from 'Constants';

import './style.scss';
import BulkModal from './BulkModal';

const handlePaginationNavigation = (filters, getAdminOnboardingProfiles, defaultParams) => {
  getAdminOnboardingProfiles({ ...filters, ...defaultParams });
};

const columns = [
  {
    name: I18n.t('admin:onboardingTable.user_name'),
    field: 'name',
    itemAlign: TEXT_ALIGN.START,
    headerAlign: ALIGNMENT.START,
    width: '50%'
  },
  {
    name: I18n.t('admin:onboardingTable.badge_number'),
    field: 'badgeNumbers',
    itemAlign: TEXT_ALIGN.START,
    headerAlign: ALIGNMENT.START,
    width: '50%'
  }
];

const navigateToReleaseBlock = updateTransientProps => {
  updateTransientProps({ fromDashboard: true });
  NavigationService.navigate({ name: PAGE_NAMES.BULK_RELEASE_BLOCK });
};

const navigateToCreateUser = resetTransient => {
  resetTransient();
  NavigationService.navigate({ name: PAGE_NAMES.CREATE_USER });
};

const Dashboard = ({
  adminFilters = {},
  allCampaignsHaveSystemBadges = false,
  block = '',
  campaigns = [],
  checkSendWelcomeEmail = mockFunction,
  clients = [],
  custom_footer = '',
  declineProfiles = mockFunction,
  email = '',
  email_error_message = '',
  email_has_error = false,
  errorsModal = false,
  extendVetting = mockFunction,
  extendVettingBannerContent = '',
  filters = {},
  getAdminOnboardingProfiles = mockFunction,
  getAdminOnboardingProfilesLoading = false,
  getCampaignsByClient = mockFunction,
  getOnboardingBlocks = mockFunction,
  getUserOnboardingProfile = mockFunction,
  onboardings = {},
  profilesWithErrors = [],
  profilesWithoutErrors = '',
  reactivateProfileBannerContent = '',
  reactivateProfiles = mockFunction,
  rejectVetting = mockFunction,
  rejectVettingBannerContent = '',
  releaseBlockBannerContent = '',
  reOnboard = mockFunction,
  reOnboardLoading = false,
  resetTransient = mockFunction,
  selected_client = '',
  selected_signer = '',
  selectedCampaigns = [],
  selectedProfilesForReOnboard = mockFunction,
  selectedRows = [],
  sendWelcomeEmail = mockFunction,
  showExtendVettingBanner = false,
  showReactivateProfileBanner = false,
  showRejectVettingBanner = false,
  showReleaseBlockBanner = false,
  showReonboardModal = false,
  showWelcomeEmailBanner = false,
  showWelcomeEmailModal = false,
  signers = [],
  transient = {},
  updateAdminProps = mockFunction,
  updateTransientProps = mockFunction,
  updateTransientPropWithValidations = mockFunction,
  updateUserProps = mockFunction,
  user = {},
  user_role = '',
  userAdminFilters = {},
  welcomeEmailBannerContent = ''
}) => {
  const { meta } = onboardings;
  const isOneRowSelected = selectedRows?.length === 1;
  const userEmail = isOneRowSelected ? selectedRows[0].email : '';
  const noneIsSelected = selectedRows?.length <= 0;
  const isNotAdmin = user_role !== ADMIN_ROLES.ADMIN;

  const handleDrawerButtonAction = row => {
    getUserOnboardingProfile(row.id, 'seeDetails');
    getOnboardingBlocks(row.id);
    updateAdminProps({ selectedUser: [row] });
    updateTransientProps({ fromDashboard: false, selectedRows: [] });
  };

  const bubbleActions = [
    {
      label: I18n.t('admin:menuItems.sendWelcomeEmail'),
      disabled: noneIsSelected,
      onClick: checkSendWelcomeEmail.bind(null, { email: userEmail, selectedRows })
    },
    {
      label: I18n.t('admin:menuItems.declineVetting'),
      disabled: noneIsSelected,
      onClick: declineProfiles.bind(null, selectedRows)
    },
    {
      label: I18n.t('admin:menuItems.extendVetting'),
      disabled: noneIsSelected,
      onClick: extendVetting.bind(null, selectedRows)
    },
    {
      label: I18n.t('admin:menuItems.rejectVetting'),
      disabled: noneIsSelected,
      onClick: rejectVetting.bind(null, selectedRows)
    },
    {
      label: I18n.t('admin:menuItems.reOnboard'),
      disabled: noneIsSelected,
      onClick: selectedProfilesForReOnboard.bind(null, selectedRows)
    },
    {
      label: I18n.t('admin:menuItems.reActivate'),
      disabled: noneIsSelected,
      onClick: reactivateProfiles.bind(null, selectedRows)
    },
    {
      label: I18n.t('admin:menuItems.releaseBlock'),
      disabled: noneIsSelected,
      onClick: navigateToReleaseBlock.bind(null, updateTransientProps)
    }
  ];

  const renderBanner = () => {
    const banners = [
      {
        content: I18n.t('admin:successBannerMessages.welcomeEmail', {
          welcomeEmailBannerContent
        }),
        show: showWelcomeEmailBanner,
        onClose: updateTransientProps.bind(null, { showWelcomeEmailBanner: false })
      },
      {
        content: I18n.t('admin:successBannerMessages.extendVetting', {
          extendVettingBannerContent
        }),
        show: showExtendVettingBanner,
        onClose: updateTransientProps.bind(null, { showExtendVettingBanner: false })
      },
      {
        content: I18n.t('admin:successBannerMessages.rejectVetting', {
          rejectVettingBannerContent
        }),
        show: showRejectVettingBanner,
        onClose: updateTransientProps.bind(null, { showRejectVettingBanner: false })
      },
      {
        content: I18n.t('admin:successBannerMessages.reactivateProfile', {
          reactivateProfileBannerContent
        }),
        show: showReactivateProfileBanner,
        onClose: updateTransientProps.bind(null, { showReactivateProfileBanner: false })
      },
      {
        content: I18n.t('admin:successBannerMessages.blockRelease', {
          releaseBlockBannerContent,
          blockNumber: block
        }),
        show: showReleaseBlockBanner,
        onClose: updateTransientProps.bind(null, { showReleaseBlockBanner: false })
      }
    ];

    const activeBanner = banners.find(banner => banner.show);

    useEffect(() => {
      getAdminOnboardingProfiles({ active_users: true, active_onboarding: true, per_page: TABLE_CONFIG.ROWS });
    }, []);

    return (
      <>
        {activeBanner && (
          <Banner
            type={ALERT_TYPES.SUCCESS}
            content={activeBanner.content}
            show={activeBanner}
            timer={Config.GROWL_AUTOHIDE}
            onClose={activeBanner.onClose}
          />
        )}
      </>
    );
  };

  return (
    <Page id={'dashboard'}>
      <AppHeader
        hideAvatar
        showMcPins={isNotAdmin}
        user
        banner={renderBanner()}
        actions={bubbleActions}
        goBack
        handleClick={NavigationService.navigate.bind(null, { name: PAGE_NAMES.ADMIN })}
        onClickMcPin={user?.mc_pins?.length > 1 ? updateTransientProps.bind(null, { mcPinsModal: true }) : mockFunction}
        title={I18n.t('admin:dashboard')}
      />

      <div className="table-container">
        <QuickFilters
          filtersType={FILTERS.ONBOARDING}
          fetchTableItems={getAdminOnboardingProfiles}
          quickFilters={userAdminFilters?.onboarding ? userAdminFilters : adminFilters}
          role={user_role}
          transient={transient}
          updateUserProps={updateUserProps}
          updateTransientProps={updateTransientProps}
        />

        <Table
          headerHeight={'auto'}
          moreInfo={true}
          rows={onboardings}
          columns={columns}
          processing={getAdminOnboardingProfilesLoading}
          userRole={user_role}
          drawerButtonAction={handleDrawerButtonAction}
        />

        <ColumnView width={'100%'} paddingBottom={65}>
          <Pagination
            loading={getAdminOnboardingProfilesLoading}
            totalNumberOfPages={meta?.total_pages}
            onNavigate={handlePaginationNavigation.bind(null, filters, getAdminOnboardingProfiles)}
            numberOfElements={TABLE_CONFIG.ROWS}
            externalPageNumber={meta?.page}
          />
        </ColumnView>
      </div>

      <Fab className={'w-full px-16'} position={'center-bottom'} slot={'fixed'}>
        <Button.Outline width={'100%'} iconLeft={<Plus />} onClick={navigateToCreateUser.bind(null, resetTransient)}>
          {I18n.t('admin:createUser')}
        </Button.Outline>
      </Fab>

      <BulkModal
        allCampaignsHaveSystemBadges={allCampaignsHaveSystemBadges}
        campaigns={campaigns}
        clients={clients}
        custom_footer={custom_footer}
        email_error_message={email_error_message}
        email_has_error={email_has_error}
        email={email}
        errorsModal={errorsModal}
        getCampaignsByClient={getCampaignsByClient}
        isOneRowSelected={isOneRowSelected}
        profilesWithErrors={profilesWithErrors}
        profilesWithoutErrors={profilesWithoutErrors}
        reOnboard={reOnboard}
        reOnboardLoading={reOnboardLoading}
        selected_client={selected_client}
        selected_signer={selected_signer}
        selectedCampaigns={selectedCampaigns}
        selectedRows={selectedRows}
        sendWelcomeEmail={sendWelcomeEmail}
        showReonboardModal={showReonboardModal}
        showWelcomeEmailModal={showWelcomeEmailModal}
        signers={signers}
        updateTransientProps={updateTransientProps}
        updateTransientPropWithValidations={updateTransientPropWithValidations}
      />
    </Page>
  );
};

Dashboard.propTypes = {
  allCampaignsHaveSystemBadges: PropTypes.bool,
  adminFilters: PropTypes.object,
  block: PropTypes.string,
  campaigns: PropTypes.array,
  checkSendWelcomeEmail: PropTypes.func,
  clients: PropTypes.array,
  custom_footer: PropTypes.string,
  declineProfiles: PropTypes.func,
  email_error_message: PropTypes.string,
  email_has_error: PropTypes.bool,
  email: PropTypes.string,
  errorsModal: PropTypes.bool,
  extendVetting: PropTypes.func,
  extendVettingBannerContent: PropTypes.string,
  filters: PropTypes.object,
  getAdminOnboardingProfiles: PropTypes.func,
  getAdminOnboardingProfilesLoading: PropTypes.bool,
  getCampaignsByClient: PropTypes.func,
  getOnboardingBlocks: PropTypes.func,
  getUserOnboardingProfile: PropTypes.func,
  onboardings: PropTypes.object,
  profilesWithErrors: PropTypes.array,
  profilesWithoutErrors: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  reactivateProfileBannerContent: PropTypes.string,
  reactivateProfiles: PropTypes.func,
  rejectVetting: PropTypes.func,
  rejectVettingBannerContent: PropTypes.string,
  releaseBlockBannerContent: PropTypes.string,
  reOnboard: PropTypes.func,
  reOnboardLoading: PropTypes.bool,
  resetTransient: PropTypes.func,
  reuse_existing_badge_number: PropTypes.bool,
  selected_client: PropTypes.string,
  selected_signer: PropTypes.string,
  selectedCampaigns: PropTypes.array,
  selectedProfilesForReOnboard: PropTypes.func,
  selectedRows: PropTypes.array,
  sendWelcomeEmail: PropTypes.func,
  showExtendVettingBanner: PropTypes.bool,
  showReactivateProfileBanner: PropTypes.bool,
  showRejectVettingBanner: PropTypes.bool,
  showReleaseBlockBanner: PropTypes.bool,
  showReonboardModal: PropTypes.bool,
  showWelcomeEmailBanner: PropTypes.bool,
  showWelcomeEmailModal: PropTypes.bool,
  signers: PropTypes.array,
  transient: PropTypes.object,
  updateAdminProps: PropTypes.func,
  updateTransientProps: PropTypes.func,
  updateTransientPropWithValidations: PropTypes.func,
  updateUserProps: PropTypes.func,
  user_role: PropTypes.string,
  user: PropTypes.object,
  userAdminFilters: PropTypes.object,
  welcomeEmailBannerContent: PropTypes.string
};

export default Dashboard;
